import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Stack, capitalize, Box } from "@mui/material";
import { StatCardTypography } from "Components/UI/StatCard";
import { useMemo, useState } from "react";
import { formatNumber } from "Helpers/numbers";
import {
  AnalyticsIncreaseDecreaseStatCard,
  AnalyticsStatCard,
  metricByAttribution,
  metricConfigByName,
  percentageTopOfTotal,
} from "features/analytics";
import { AttributionMetric } from "features/analytics/types/types";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useAttributionContext } from "Providers/AttributionProvider";

interface IAttributionHeaderProps {
  chartKey: string;
  dimension: ApiDimension;
  title: string;
  attributes: ApiChartAttribute[];
}

function AttributionHeader({
  chartKey,
  dimension,
  title,
  attributes,
}: IAttributionHeaderProps) {
  const metricToName = useMetricName();
  const [metric, setMetric] = useState<ApiMetric>(
    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
  );
  const { model } = useAttributionContext();

  // Response data is keyed by the model-specific metric; chart config stays base
  const dataMetric = useMemo(() => {
    if (model === undefined) {
      return metric;
    }
    const map = metricConfigByName(metric);
    if (!map?.attribtionMetric) {
      return metric;
    }
    return (
      metricByAttribution(
        model || ApiAttributionModel.EBA,
        map.attribtionMetric as AttributionMetric,
      ) || metric
    );
  }, [model, metric]);

  return (
    <Box
      sx={{
        display: "grid",
        gap: AnalyticsSpacing,
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 1fr) minmax(0, 3fr)",
        },
        alignItems: "stretch",
        width: "100%",
        minWidth: 0,
      }}
    >
      <Stack
        spacing={AnalyticsSpacing}
        direction={{ xs: "column", sm: "row", md: "column" }}
        sx={{ minWidth: 0 }}
      >
        <AnalyticsStatCard
          height={140}
          chart={{
            name: "",
            dimension: [dimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
            ],
          }}
          renderBody={(data) => (
            <>
              <StatCardTypography size="xs" mb={1}>
                Highest Performing <br />
                {title} by {metricToName(metric)}:
              </StatCardTypography>
              <StatCardTypography size="large" lines={1}>
                {data[0]?.name}
              </StatCardTypography>
            </>
          )}
        />
        <AnalyticsStatCard
          height={140}
          chart={{
            name: "",
            dimension: [dimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
            ],
          }}
          renderBody={(data) => {
            return (
              <>
                <StatCardTypography size="xs" mb={1}>
                  Generating
                </StatCardTypography>
                <StatCardTypography size="large" mb={1}>
                  {formatNumber(percentageTopOfTotal(data, dataMetric))}%
                </StatCardTypography>
                <StatCardTypography size="xs">
                  of Total {metricToName(metric)}
                </StatCardTypography>
              </>
            );
          }}
        />
        <AnalyticsIncreaseDecreaseStatCard
          metric={metric}
          height={120}
          chart={{
            name: "",
            dimension: [dimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [metric as ApiMetric],
              },
            ],
          }}
        />
      </Stack>

      <Box sx={{ minHeight: { xs: 360, md: 420 }, minWidth: 0, width: "100%" }}>
        <StandardAnalyticsCard
          chartKey={chartKey}
          onChangeMetric={(m) => setMetric(m)}
          chart={{
            name: `Top 5 ${capitalize(title)} by`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY, dimension],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes,
            limit: 5,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                stackId: "l",
                chart: {
                  [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_SALES_COUNT]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_ROI]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_ROI_LTV]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_ROAS]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_ROAS_LTV]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_PAGEVIEWS_COUNT]: ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT]:
                    ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              availableMetrics: [
                ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                ApiMetric.METRIC_SALES_COUNT,
                ApiMetric.METRIC_REVENUE,
                ApiMetric.METRIC_ROI,
                ApiMetric.METRIC_ROI_LTV,
                ApiMetric.METRIC_ROAS,
                ApiMetric.METRIC_ROAS_LTV,
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
              ],
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default AttributionHeader;
